import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Loader2, Sparkles, ArrowLeft, ArrowRight, ExternalLink, CheckCircle2, Clock,
  ShoppingBag, Calendar, MessageSquare, BarChart3, Users, MapPin, Image as ImageIcon,
  FileText, Zap, Shield, Mail, Wrench, Globe, Palette, BookOpen, Search, TrendingUp,
  Bell, Wallet, Tag, QrCode, Star, FormInput, Database, LayoutDashboard, Rocket,
  Trophy, Tv, Gamepad2, IdCard, Map as MapIcon, Heart, Gift, ShirtIcon, ChartBar, PlayCircle,
  Eye, Filter, Megaphone, Building2,
} from "lucide-react";
import { api } from "../lib/api";
import { WeCockpitLogo } from "../components/branding/WeCockpitLogo";
import PublicHeader from "../components/branding/PublicHeader";

/**
 * CockpitFeaturesPage — Page publique listant toutes les capacités WECOCKPIT.
 * URL : /cockpit-features (publique, sans auth)
 */

// Catalogue statique des fonctionnalités plateforme (gérées par COCKPIT lui-même)
const PLATFORM_FEATURES = [
  {
    category: "Gestion de contenu dynamique",
    color: "from-sky-500 to-cyan-500",
    items: [
      { icon: FileText, title: "Pages & contenus dynamiques", desc: "Créez et modifiez vos pages depuis votre back-office, sans toucher au code." },
      { icon: BookOpen, title: "Blog complet", desc: "Articles avec catégories, SEO, partage social, brouillons et programmation." },
      { icon: ImageIcon, title: "Médiathèque", desc: "Bibliothèque centralisée d'images, optimisation auto, renommage SEO." },
      { icon: Wrench, title: "Catalogue de prestations", desc: "Services structurés avec descriptions, tarifs, photos, FAQ." },
      { icon: Star, title: "Réalisations & témoignages", desc: "Galerie de projets passés, témoignages clients, notes." },
    ],
  },
  {
    category: "Suivi des leads & conversion",
    color: "from-emerald-500 to-green-500",
    items: [
      { icon: FormInput, title: "Formulaires de contact intelligents", desc: "Captation des demandes de devis avec anti-spam, scoring." },
      { icon: Users, title: "CRM intégré", desc: "Toutes vos demandes centralisées : prospects, suivi, relances, notes internes." },
      { icon: Bell, title: "Notifications temps réel", desc: "Alerte email + dashboard à chaque nouveau lead." },
      { icon: Mail, title: "Emails automatiques", desc: "Confirmations, relances, rappels — design pro avec votre charte." },
    ],
  },
  {
    category: "Performances & analytics",
    color: "from-violet-500 to-purple-500",
    items: [
      { icon: BarChart3, title: "Dashboard analytique", desc: "Visites, conversions, leads, ventes — sur 24h, 7j, 30j, 90j, 1 an, depuis création." },
      { icon: TrendingUp, title: "Évolution mensuelle", desc: "Graphiques par mois, comparaison périodes, exports CSV/PDF." },
      { icon: Search, title: "Suivi SEO local", desc: "Tracking de votre positionnement Google sur vos mots-clés cibles." },
      { icon: MapPin, title: "Pages locales SEO", desc: "Pages dédiées par zone d'intervention pour le référencement géographique." },
    ],
  },
  {
    category: "Design & branding",
    color: "from-pink-500 to-rose-500",
    items: [
      { icon: Palette, title: "Design sur-mesure", desc: "Charte graphique appliquée à 100% — couleurs de marque, typographies, ambiance, photos optimisées." },
      { icon: LayoutDashboard, title: "Sector Presets", desc: "Templates pré-configurés par métier (plomberie, restaurant, beauté, immobilier…)." },
      { icon: Globe, title: "Domaine personnalisé", desc: "Votre site sur votre nom de domaine, HTTPS automatique, redirections gérées." },
    ],
  },
  {
    category: "Sécurité & conformité",
    color: "from-amber-500 to-orange-500",
    items: [
      { icon: Shield, title: "RGPD natif", desc: "Bandeau cookies, mentions légales, CGV, formulaire de droit d'accès." },
      { icon: Shield, title: "Anti-bot & anti-spam", desc: "Protection contre le scraping, le brute-force et les bots malveillants." },
      { icon: Database, title: "Backups automatiques", desc: "Sauvegardes quotidiennes des données et médias, restauration possible." },
    ],
  },
  {
    category: "Pilotage agence (HUB)",
    color: "from-slate-500 to-gray-600",
    items: [
      { icon: LayoutDashboard, title: "HUB centralisé", desc: "Tous vos sites clients pilotés depuis une seule console." },
      { icon: Zap, title: "SSO 1-clic", desc: "Connexion en 1 clic au back-office de chaque client depuis le HUB." },
      { icon: Mail, title: "Facturation automatisée", desc: "Génération PDF des factures, abonnements récurrents, suivi paiements." },
    ],
  },
];

export default function CockpitFeaturesPage() {
  const [widgets, setWidgets] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [agency, setAgency] = useState({});
  const [activeCategory, setActiveCategory] = useState("all");
  const location = useLocation();

  useEffect(() => {
    Promise.all([
      api.get("/agency/public/widget-catalog"),
      api.get("/settings"),
      api.get("/agency/public/plans").catch(() => ({ data: [] })),
    ]).then(([wRes, sRes, pRes]) => {
      const items = Array.isArray(wRes.data) ? wRes.data : (wRes.data?.items || []);
      setWidgets(items);
      setAgency(sRes.data || {});
      const planRows = Array.isArray(pRes.data) ? pRes.data : (pRes.data?.plans || []);
      setPlans(planRows.filter((p) => p.is_active));
    }).catch(() => setWidgets([])).finally(() => setLoading(false));
  }, []);

  // Scroll vers l'ancre #widgets (ou autre) après chargement
  useEffect(() => {
    if (loading) return;
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
      }
    }
  }, [loading, location.hash]);

  const agencyName = agency.agency_brand_name || agency.agency_name || "Agence MSC";
  const logoUrl = agency.agency_logo_url || "";

  // Catégorisation des widgets : actifs (price >= 0 et status active), à venir (status="coming_soon")
  const activeWidgets = widgets.filter((w) => w.status !== "coming_soon" && w.status !== "draft");
  const upcomingWidgets = widgets.filter((w) => w.status === "coming_soon" || w.status === "draft");

  // Catégories distinctes (pour les filtres)
  const categories = useMemo(() => {
    const seen = new Map();
    activeWidgets.forEach((w) => {
      if (w.category && !seen.has(w.category)) {
        seen.set(w.category, w.category_label || w.category);
      }
    });
    return Array.from(seen.entries()).map(([key, label]) => ({ key, label }));
  }, [activeWidgets]);

  // Widgets filtrés par catégorie
  const filteredActive = activeCategory === "all"
    ? activeWidgets
    : activeWidgets.filter((w) => w.category === activeCategory);

  // Forfait club_sport mis en avant (s'il existe)
  const sportPlan = plans.find((p) => p.code === "club_sport");

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-sky-50/40 text-slate-900" data-testid="cockpit-features-page">
      <div className="fixed inset-0 opacity-30 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-violet-200/50 rounded-full blur-3xl"/>
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-emerald-200/50 rounded-full blur-3xl"/>
      </div>

      {/* Header partagé (light tone) */}
      <PublicHeader tone="light" agencyName={agencyName}/>

      <main className="relative max-w-6xl mx-auto px-6 py-10 sm:py-16">
        {/* Hero */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white shadow-sm border border-slate-200 text-xs font-semibold uppercase tracking-wider mb-4 text-slate-700">
            <Sparkles className="w-3 h-3 text-emerald-500"/> Plateforme WECOCKPIT
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-4 bg-gradient-to-br from-slate-900 via-slate-700 to-sky-700 bg-clip-text text-transparent">
            Tout ce que WECOCKPIT sait faire
          </h1>
          <p className="text-base sm:text-lg text-slate-600 max-w-3xl mx-auto">
            Une plateforme complète pour créer, piloter et faire grandir votre site web — modules à la carte, données en temps réel.
          </p>
          <div className="mt-3 text-xs text-slate-500">
            Solution opérée par <span className="font-bold text-slate-700">{agencyName}</span>
          </div>
        </div>

        {/* === SECTION FONCTIONNALITÉS PLATEFORME === */}
        <section className="mb-20">
          <div className="flex items-center gap-3 mb-8">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent to-slate-300"/>
            <h2 className="text-2xl font-bold uppercase tracking-wider text-slate-900 px-4">Fonctionnalités plateforme</h2>
            <div className="h-px flex-1 bg-gradient-to-l from-transparent to-slate-300"/>
          </div>

          <div className="space-y-12">
            {PLATFORM_FEATURES.map((cat, idx) => (
              <div key={idx} data-testid={`feature-category-${idx}`}>
                <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r ${cat.color} mb-5 shadow-sm`}>
                  <span className="text-xs font-bold uppercase tracking-wider text-white">{cat.category}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {cat.items.map((item, j) => {
                    const Icon = item.icon;
                    return (
                      <div
                        key={j}
                        className="bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-lg hover:border-slate-300 hover:-translate-y-0.5 transition"
                      >
                        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${cat.color} flex items-center justify-center mb-3 shadow-sm`}>
                          <Icon className="w-5 h-5 text-white"/>
                        </div>
                        <h4 className="font-bold text-slate-900 mb-1">{item.title}</h4>
                        <p className="text-xs text-slate-600 leading-relaxed">{item.desc}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* === BANDEAU FORFAIT CLUB DE SPORT (si dispo) === */}
        {sportPlan && (
          <section className="mb-12 scroll-mt-20" id="club-sport">
            <div className="relative overflow-hidden rounded-3xl shadow-2xl bg-gradient-to-br from-violet-700 via-purple-700 to-pink-600 p-7 sm:p-10 text-white">
              <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-pink-400/20 blur-3xl"/>
              <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-violet-400/20 blur-3xl"/>
              <div className="relative grid md:grid-cols-[1fr_auto] gap-6 items-center">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur text-[10px] font-black uppercase tracking-wider mb-3">
                    <Trophy className="w-3.5 h-3.5"/> Pack vedette
                  </div>
                  <h2 className="text-3xl sm:text-4xl font-black leading-tight mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.04em" }}>
                    {sportPlan.name}
                  </h2>
                  <p className="text-sm text-white/85 max-w-2xl leading-relaxed">{sportPlan.description}</p>
                  <div className="flex flex-wrap items-center gap-4 mt-4">
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-wider text-white/70">Abonnement</div>
                      <div className="text-3xl font-black tabular-nums">{(sportPlan.monthly_price_cents / 100).toFixed(0)} € <span className="text-sm font-bold text-white/80">/mo HT</span></div>
                    </div>
                    <div className="h-10 w-px bg-white/20"/>
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-wider text-white/70">Setup</div>
                      <div className="text-2xl font-black tabular-nums">{(sportPlan.setup_fee_cents / 100).toFixed(0)} € <span className="text-xs font-bold text-white/80">HT</span></div>
                    </div>
                    <div className="h-10 w-px bg-white/20"/>
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-wider text-white/70">Widgets inclus</div>
                      <div className="text-2xl font-black tabular-nums">{(sportPlan.included_widgets || []).length}</div>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <Link
                    to="/sport"
                    target="_blank"
                    rel="noopener"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-white text-violet-700 font-black uppercase tracking-wider text-sm hover:scale-[1.02] transition shadow-lg"
                    data-testid="club-sport-demo-link"
                  >
                    <PlayCircle className="w-4 h-4"/>Voir le site démo<ExternalLink className="w-3.5 h-3.5"/>
                  </Link>
                  <Link
                    to="/onboarding"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-white/10 backdrop-blur text-white font-bold uppercase tracking-wider text-xs hover:bg-white/20 transition border border-white/30"
                  >
                    <Rocket className="w-4 h-4"/>Demander un devis
                  </Link>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* === SECTION WIDGETS ACTIFS — avec filtres catégorie === */}
        <section id="widgets" className="mb-16 scroll-mt-20">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent to-emerald-400"/>
            <h2 className="text-2xl font-bold uppercase tracking-wider text-emerald-700 px-4 inline-flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5"/> {filteredActive.length} widgets disponibles
            </h2>
            <div className="h-px flex-1 bg-gradient-to-l from-transparent to-emerald-400"/>
          </div>

          {/* Filtres catégories */}
          {!loading && categories.length > 0 && (
            <div className="flex flex-wrap items-center justify-center gap-2 mb-8" data-testid="widget-category-filters">
              <button
                type="button"
                onClick={() => setActiveCategory("all")}
                className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition border ${
                  activeCategory === "all"
                    ? "bg-slate-900 text-white border-slate-900 shadow"
                    : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                }`}
                data-testid="widget-filter-all"
              >
                <Filter className="w-3 h-3"/>Tout ({activeWidgets.length})
              </button>
              {categories.map((c) => {
                const cnt = activeWidgets.filter((w) => w.category === c.key).length;
                const sel = activeCategory === c.key;
                const cat = CATEGORY_COLORS[c.key] || CATEGORY_COLORS.content;
                return (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => setActiveCategory(c.key)}
                    className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition border ${
                      sel
                        ? `bg-gradient-to-r ${cat.grad} text-white border-transparent shadow-md`
                        : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                    }`}
                    data-testid={`widget-filter-${c.key}`}
                  >
                    {c.label} <span className={sel ? "text-white/80" : "text-slate-400"}>({cnt})</span>
                  </button>
                );
              })}
            </div>
          )}

          {loading ? (
            <div className="text-center py-12"><Loader2 className="w-6 h-6 mx-auto animate-spin text-emerald-500"/></div>
          ) : filteredActive.length === 0 ? (
            <div className="text-center py-8 text-slate-500 text-sm italic">Aucun widget pour cette catégorie.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="widget-grid">
              {filteredActive.map((w) => (
                <WidgetCard key={w.widget_key || w.id} widget={w} active/>
              ))}
            </div>
          )}
        </section>

        {/* === SECTION WIDGETS À VENIR === */}
        {upcomingWidgets.length > 0 && (
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-8">
              <div className="h-px flex-1 bg-gradient-to-r from-transparent to-amber-400"/>
              <h2 className="text-2xl font-bold uppercase tracking-wider text-amber-700 px-4 inline-flex items-center gap-2">
                <Clock className="w-5 h-5"/> En cours de développement
              </h2>
              <div className="h-px flex-1 bg-gradient-to-l from-transparent to-amber-400"/>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {upcomingWidgets.map((w) => (
                <WidgetCard key={w.widget_key || w.id} widget={w} active={false}/>
              ))}
            </div>
          </section>
        )}

        {/* CTA */}
        <div className="text-center bg-gradient-to-r from-sky-500 to-emerald-500 rounded-2xl p-8 shadow-xl">
          <h2 className="text-2xl font-bold mb-2 text-white">Lancez votre projet avec WECOCKPIT</h2>
          <p className="text-sm text-white/90 mb-5 max-w-2xl mx-auto">
            Discutons de votre projet. Devis personnalisé en 5 min via notre onboarding guidé.
          </p>
          <Link
            to="/onboarding"
            className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-white text-slate-900 font-bold hover:bg-slate-50 transition shadow-md"
            data-testid="features-cta-onboarding"
          >
            <Rocket className="w-4 h-4"/> Démarrer mon projet <ArrowRight className="w-4 h-4"/>
          </Link>
        </div>
      </main>

      <footer className="relative max-w-6xl mx-auto px-6 py-8 text-center text-xs text-slate-500">
        © {new Date().getFullYear()} WECOCKPIT — Solution opérée par {agencyName}
      </footer>
    </div>
  );
}


// Mapping icon par widget_key (fallback Sparkles)
const WIDGET_ICONS = {
  // shop
  shop_enabled: ShoppingBag,
  shop_click_collect_enabled: ShoppingBag,
  shop_product_perso_enabled: ShirtIcon,
  shop_checkout_full_page_enabled: ShoppingBag,
  shop_pickup_event_enabled: MapPin,
  shop_donation_enabled: Heart,
  shop_quicksize_enabled: Tag,
  food_mode_enabled: Tag,
  b2b_enabled: Building2,
  loyalty_enabled: Wallet,
  // booking
  booking_enabled: Calendar,
  rental_enabled: Calendar,
  // directory
  directory_partners_enabled: Users,
  directory_court_map_enabled: MapIcon,
  org_chart_enabled: Users,
  business_offers_b2b_enabled: Gift,
  business_events_b2b_enabled: Calendar,
  partner_brochure_pdf_enabled: FileText,
  // team_data
  team_data_lnh_enabled: Trophy,
  team_data_fff_enabled: Trophy,
  team_roster_enabled: Users,
  player_profile_enabled: Star,
  match_calendar_enabled: Calendar,
  next_match_card_enabled: Clock,
  // match_live
  match_live_perfs_wall_enabled: Tv,
  match_live_operator_enabled: ChartBar,
  match_live_speaker_enabled: Megaphone,
  match_live_demo_mode_enabled: PlayCircle,
  marquee_partners_enabled: Megaphone,
  // engagement
  match_pronostic_enabled: Gamepad2,
  match_mvp_vote_enabled: Star,
  contests_enabled: Trophy,
  halftime_offers_enabled: Gift,
  // clients
  customer_supporter_space_enabled: Users,
  customer_partner_space_enabled: Building2,
  customer_member_space_enabled: IdCard,
  digital_member_card_enabled: IdCard,
  member_schedule_enabled: Calendar,
  member_offers_enabled: Gift,
  jersey_back_perso_enabled: ShirtIcon,
  wishlist_enabled: Heart,
  // content
  news_aggregator_enabled: BookOpen,
  youtube_videos_enabled: PlayCircle,
  history_timeline_enabled: BookOpen,
  rgpd_generator_enabled: Shield,
  // marketing
  popup_callback_enabled: Bell,
  agency_white_label_credit_enabled: Rocket,
  // legacy
  reviews_enabled: Star,
  click_collect_enabled: ShoppingBag,
  qr_labels_enabled: QrCode,
  rewards_enabled: Wallet,
  blog_enabled: BookOpen,
  newsletter_enabled: Mail,
  testimonials_enabled: Star,
  team_enabled: Users,
  realisations_enabled: ImageIcon,
  faq_enabled: MessageSquare,
  zones_enabled: MapPin,
  local_seo_enabled: Search,
  rank_tracker_enabled: TrendingUp,
};

// Couleurs par catégorie (gradient + accent)
const CATEGORY_COLORS = {
  shop: { grad: "from-pink-500 to-rose-500", accent: "rose" },
  booking: { grad: "from-sky-500 to-cyan-500", accent: "sky" },
  clients: { grad: "from-violet-500 to-purple-500", accent: "violet" },
  directory: { grad: "from-emerald-500 to-teal-500", accent: "emerald" },
  team_data: { grad: "from-blue-500 to-indigo-500", accent: "blue" },
  match_live: { grad: "from-amber-500 to-orange-500", accent: "amber" },
  engagement: { grad: "from-fuchsia-500 to-pink-600", accent: "fuchsia" },
  content: { grad: "from-slate-500 to-gray-600", accent: "slate" },
  marketing: { grad: "from-lime-500 to-green-500", accent: "lime" },
};

// Routes nécessitant une auth ou inadaptées à un iframe preview (Stripe, OAuth).
// Pour celles-ci, on affiche un fallback animé au lieu de l'iframe live.
const PREVIEW_BLACKLIST = new Set([
  "shop_checkout_full_page_enabled",      // Stripe-friendly flow, dépend du panier
  "shop_pickup_event_enabled",            // idem checkout
  "shop_donation_enabled",                // idem checkout
  "shop_click_collect_enabled",           // idem checkout
  "agency_white_label_credit_enabled",    // login form OAuth
  "match_live_operator_enabled",          // PIN required → page vide intéressante mais vide
  "match_live_speaker_enabled",           // idem
  "team_data_fff_enabled",                // placeholder/coming soon
]);

// Hook : déclenche `setVisible(true)` quand l'élément entre dans la viewport.
function useInView(ref, rootMargin = "200px") {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current || inView) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      },
      { rootMargin }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref, rootMargin, inView]);
  return inView;
}

function WidgetPreview({ widget, cat }) {
  const wrapRef = React.useRef(null);
  const inView = useInView(wrapRef);
  const blocked = PREVIEW_BLACKLIST.has(widget.widget_key);
  const demoUrl = widget.demo_url || "";
  const Icon = WIDGET_ICONS[widget.widget_key] || Sparkles;

  return (
    <div
      ref={wrapRef}
      className={`relative overflow-hidden rounded-xl mb-3 bg-gradient-to-br ${cat.grad} aspect-video group/preview`}
      data-testid={`widget-preview-${widget.widget_key}`}
    >
      {/* État 1 : pas blacklisté + en viewport → iframe live miniature scalée */}
      {inView && !blocked && demoUrl ? (
        <div className="absolute inset-0 pointer-events-none">
          <iframe
            src={demoUrl}
            title={widget.label}
            loading="lazy"
            sandbox="allow-scripts allow-same-origin"
            className="absolute top-0 left-0 origin-top-left"
            style={{
              width: "250%",
              height: "250%",
              transform: "scale(0.4)",
              border: "none",
              background: "white",
            }}
          />
          {/* Overlay sombre + label au hover */}
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/preview:opacity-100 transition flex items-center justify-center">
            <span className="text-white text-xs font-black uppercase tracking-wider inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur border border-white/30">
              <Eye className="w-3.5 h-3.5"/> Live preview
            </span>
          </div>
        </div>
      ) : (
        // État 2 : placeholder élégant (fallback ou avant inView)
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="absolute inset-0 opacity-30">
            <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/40 blur-3xl"/>
            <div className="absolute -bottom-10 -left-10 w-40 h-40 rounded-full bg-white/30 blur-3xl"/>
          </div>
          <div className="relative text-center text-white px-4">
            <div className="inline-flex w-12 h-12 rounded-2xl bg-white/20 backdrop-blur items-center justify-center mb-2 shadow-lg">
              <Icon className="w-6 h-6 text-white"/>
            </div>
            <div className="text-[10px] font-bold uppercase tracking-wider opacity-90">
              {blocked ? "Démo interactive" : "Chargement aperçu…"}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function WidgetCard({ widget, active }) {
  const Icon = WIDGET_ICONS[widget.widget_key] || Sparkles;
  const price = widget.monthly_price_cents > 0
    ? `+${(widget.monthly_price_cents / 100).toFixed(0)} €/mois`
    : "Inclus";
  const cat = CATEGORY_COLORS[widget.category] || CATEGORY_COLORS.content;
  const badgeCls = active
    ? "bg-emerald-100 text-emerald-700 border-emerald-200"
    : "bg-amber-100 text-amber-700 border-amber-200";
  const demoUrl = widget.demo_url || "";
  const demoLabel = widget.demo_label || "Voir la démo";

  return (
    <div
      className={`group bg-white border rounded-2xl p-4 hover:shadow-xl hover:-translate-y-1 transition relative overflow-hidden ${active ? "border-slate-200 hover:border-slate-300" : "border-amber-200 hover:border-amber-400"}`}
      data-testid={`widget-card-${widget.widget_key}`}
    >
      {/* Live preview */}
      {active && <WidgetPreview widget={widget} cat={cat}/>}

      <div className="flex items-start justify-between gap-2 mb-2 relative">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center shadow-md bg-gradient-to-br ${cat.grad}`}>
          <Icon className="w-4 h-4 text-white"/>
        </div>
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${badgeCls}`}>
          {active ? price : "Bientôt"}
        </span>
      </div>
      <h4 className="font-bold text-slate-900 mb-1 leading-snug text-sm">{widget.label || widget.widget_key}</h4>
      {widget.description && (
        <p className="text-xs text-slate-600 leading-relaxed line-clamp-2 mb-2">{widget.description}</p>
      )}
      <div className="flex items-center gap-2 mt-2">
        {demoUrl && active ? (
          <Link
            to={demoUrl}
            target="_blank"
            rel="noopener"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider bg-gradient-to-r ${cat.grad} text-white hover:shadow-lg transition`}
            data-testid={`widget-demo-${widget.widget_key}`}
          >
            <PlayCircle className="w-3.5 h-3.5"/>{demoLabel}<ExternalLink className="w-3 h-3"/>
          </Link>
        ) : null}
      </div>
      <div className="text-[9px] font-mono text-slate-400 mt-2 truncate uppercase">{widget.widget_key}</div>
    </div>
  );
}
